import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useAlert } from '../../../contexts/AlertContext';
import { jobApi, type JobSearchDetailData, BASE_URL } from '../../../services/api';
import TopNavigation from '../../../components/feature/TopNavigation';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import { useNavigation } from '../../../hooks/useNavigation';

export default function CaregiverJobDetail() {

    const { goBack, navigateTo } = useNavigation();
    const location = useLocation();
    const { jobId } = useParams();
    const { user } = useAuth();
    const { showAlert } = useAlert();

    // ⭐ 보낸견적에서 왔는지 확인
    const from = location.state?.from;
    const fromSentQuotes = location.state?.fromSentQuotes || false;
    const estimateNoFromState = location.state?.estimateNo;
    const oneDayMoneyFromState = location.state?.oneDayMoney;

    const [activeTab, setActiveTab] = useState<'info' | 'location' | 'requirements'>('info');
    const [jobDetail, setJobDetail] = useState<JobSearchDetailData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // 초기 데이터 로드
    useEffect(() => {
        if (jobId && user) {
            loadJobDetail();
        }
    }, [jobId, user, location.key]);

    const loadJobDetail = async () => {
        if (!user || !jobId) return;

        setIsLoading(true);
        try {
            const detail = await jobApi.getDetail({
                memberNo: user.memberNo,
                apiKey: user.apiKey,
                careNo: parseInt(jobId)
            });

            setJobDetail(detail);
            console.log('[공고 상세] 로드 성공:', detail);

        } catch (error) {
            console.error('[공고 상세] 로드 실패:', error);
            showAlert(error instanceof Error ? error.message : '공고 정보를 불러오는데 실패했습니다.', 'error');
            navigateTo(fromSentQuotes ? '/profile/sent-quotes' : '/caregiver/job-search');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {

        goBack();
        //navigate(fromSentQuotes ? '/profile/sent-quotes' : '/caregiver/job-search');
        /**
        if (from) {
            navigate(from);
        } else {
            navigate(-1);
        } */
    };

    const handleApply = () => {
        if (!jobDetail) return;

        if (fromSentQuotes && estimateNoFromState) {
            navigateTo(`/caregiver/job-detail/${jobId}/apply`, {
                fromSentQuotes: true,
                estimateNo: estimateNoFromState,
                oneDayMoney: oneDayMoneyFromState,
                careWhat: jobDetail.careWhat
            });
        } else if (jobDetail.estimateYn === 1 && jobDetail.estimateNo) {
            // ✅ state 중첩 없이 직접 전달
            navigateTo(`/caregiver/job-detail/${jobId}/apply`, {
                estimateNo: jobDetail.estimateNo,
                oneDayMoney: jobDetail.oneDayMoney,
                careWhat: jobDetail.careWhat
            });
        } else {
            navigateTo(`/caregiver/job-detail/${jobId}/apply`, {
                careWhat: jobDetail.careWhat
            });
        }
    };

    // careWhat 라벨
    const getCareWhatLabel = (careWhat: number) => {
        switch (careWhat) {
            case 0: return '단순간병';
            case 1: return '전문간병';
            case 3: return '병원동행';
            default: return '간병';
        }
    };

    // careKind 라벨
    const getCareKindLabel = (careKind: number) => {
        switch (careKind) {
            case 0: return '기간제';
            case 1: return '시간제';
            case 2: return '24시간제';
            default: return '기간제';
        }
    };

    // matchType 라벨
    const getMatchTypeLabel = (matchType: string) => {
        return matchType === 'F' ? '정찰제' : '입찰제';
    };

    // 성별 라벨
    const getGenderLabel = (gender: number) => {
        return gender === 0 ? '남성' : '여성';
    };

    // 장기요양등급 라벨
    const getRankingLabel = (ranking: number) => {
        if (ranking === 0) return '없음';
        if (ranking >= 1 && ranking <= 5) return `${ranking}등급`;
        if (ranking === 6) return '인지지원등급';
        return '없음';
    };

    // 거동가능여부 라벨
    const getMoveLabel = (moveYn: number) => {
        return moveYn === 0 ? '거동불가' : '거동가능';
    };

    // 간병 기간 계산
    const calculateDuration = (startDate: string, endDate: string) => {
        const normalizedStart = startDate.replace(/\./g, '-');
        const normalizedEnd = endDate.replace(/\./g, '-');
        const start = new Date(normalizedStart);
        const end = new Date(normalizedEnd);
        const diffTime = end.getTime() - start.getTime();
        return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1; // 당일 포함
    };

    const getPeriod = (careKind: number, careDays: string, startDate: string, endDate: string) => {
        const rawDays = careDays
            ? careDays.split(',').length
            : calculateDuration(startDate, endDate);
        return careKind === 2 ? rawDays - 1 : rawDays; // 24시간제는 박 수
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">공고 정보를 불러오는 중...</p>
                </div>
            </div>
        );
    }

    if (!jobDetail) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <i className="ri-error-warning-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-600">공고 정보를 찾을 수 없습니다.</p>
                </div>
            </div>
        );
    }

    // ⭐ 버튼 텍스트 결정
    const getButtonText = () => {

        if (fromSentQuotes) {
            // 보낸견적에서 온 경우: 입찰제(B)만 수정 가능
            return jobDetail.matchType === 'B' ? '견적 수정하기' : '지원 완료';
        }
        // 일반 공고 목록에서 온 경우
        return jobDetail.estimateYn === 1 ? '견적 수정하기' : '지원 하기';
    };

    // ⭐ 버튼 비활성화 조건
    const isButtonDisabled = fromSentQuotes && jobDetail.matchType === 'F';

    return (
        <div className="min-h-screen bg-gray-50">
            <TopNavigation
                title="상세정보"
                showBackButton
                onBackClick={handleBackClick}
            />

            <div className="pt-16 pb-24">
                {/* 공고 헤더 */}
                <div className="relative overflow-hidden p-4 mb-4" style={{ backgroundColor: '#7966FF' }}>
                    <div className="relative z-10">

                        {/* 뱃지 행 */}
                        <div className="flex items-center space-x-2 mb-2">
                            <span className="px-2 py-1 bg-white/20 rounded-md text-white text-xs font-medium">
                                {getCareWhatLabel(jobDetail.careWhat)}
                            </span>
                            <span className="px-2 py-1 bg-white/20 rounded-md text-white text-xs font-medium">
                                {getCareKindLabel(jobDetail.careKind)}
                            </span>
                            <span className={`px-2 py-1 rounded-md text-white text-xs font-medium ${jobDetail.matchType === 'F' ? 'bg-green-500' : 'bg-orange-500'
                                }`}>
                                {getMatchTypeLabel(jobDetail.matchType)}
                            </span>
                        </div>

                        {/* 환자 이름 + 금액 */}
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h2 className="text-white text-xl font-bold">{jobDetail.ptName}님</h2>
                                <p className="text-white/90 text-sm">{jobDetail.ptAge}세 • {getGenderLabel(jobDetail.ptGender)}</p>
                            </div>
                            <div className="text-right">
                                {jobDetail.matchType === 'F' ? (
                                    <>
                                        <p className="text-white/70 text-xs mb-0.5">
                                            {jobDetail.careKind === 2 ? '일당' : '시급'}
                                        </p>
                                        <p className="text-white text-xl font-bold">
                                            {jobDetail.oneDayMoney.toLocaleString()}원
                                        </p>
                                        {jobDetail.totalMoney > 0 && (
                                            <p className="text-white/70 text-xs mt-0.5">
                                                총 {jobDetail.totalMoney.toLocaleString()}원
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="text-white text-lg font-bold">견적 제출</p>
                                        <p className="text-white/80 text-xs">필요</p>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 하단 정보 박스 */}
                        <div className="bg-white/10 rounded-md p-3 space-y-2">
                            <div className="flex items-start space-x-2 text-white/90 text-sm">
                                <i className="ri-map-pin-line flex-shrink-0 mt-0.5"></i>
                                <span className="leading-snug">{jobDetail.careArea}</span>
                            </div>
                            {jobDetail.careAreaDetail && (
                                <div className="flex items-start space-x-2 text-white/90 text-sm">
                                    <i className="ri-hospital-line flex-shrink-0 mt-0.5"></i>
                                    <span className="leading-snug">{jobDetail.careAreaDetail}</span>
                                </div>
                            )}
                            <div className="flex items-center space-x-2 text-white/90 text-sm">
                                <i className="ri-calendar-line flex-shrink-0"></i>
                                <span>
                                    {jobDetail.startDate} ~ {jobDetail.endDate}
                                </span>
                                <span className="bg-white/25 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                                    {getPeriod(jobDetail.careKind, jobDetail.careDays, jobDetail.startDate, jobDetail.endDate)}일
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-4">
                    {/* 탭 메뉴 */}
                    <Card className="mb-4 px-2 py-2">
                        <div className="flex rounded-md bg-gray-100 p-1">
                            <button
                                onClick={() => setActiveTab('info')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-bold transition-colors ${activeTab === 'info'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                공고정보
                            </button>
                            <button
                                onClick={() => setActiveTab('location')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-bold transition-colors ${activeTab === 'location'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                위치
                            </button>
                            <button
                                onClick={() => setActiveTab('requirements')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-bold transition-colors ${activeTab === 'requirements'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                근무조건
                            </button>
                        </div>
                    </Card>

                    {/* 탭 콘텐츠 */}
                    {activeTab === 'info' && (
                        <div className="space-y-4">
                            {/* 보호자 정보 */}
                            <Card>
                                <h3 className="font-semibold text-gray-900 mb-4">보호자 정보</h3>
                                <div className="space-y-0">
                                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                        <div className="flex items-center space-x-2">
                                            <i className="ri-user-line text-gray-400 text-sm"></i>
                                            <span className="text-[13px] text-gray-500 uppercase tracking-wide">이름</span>
                                        </div>
                                        <span className="text-gray-900 font-medium">{jobDetail.name}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-3">
                                        <div className="flex items-center space-x-2">
                                            <i className="ri-phone-line text-gray-400 text-sm"></i>
                                            <span className="text-[13px] text-gray-500 uppercase tracking-wide">전화</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => window.location.href = `tel:${jobDetail.phone}`}
                                                className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md transition-colors"
                                            >
                                                <i className="ri-phone-line mr-1"></i>
                                                문의하기
                                            </button>
                                            {/* ⭐ 문자하기 버튼 추가 */}
                                            <button
                                                onClick={() => {
                                                    const smsUrl = `sms:${jobDetail.phone}`;
                                                    if (window.ReactNativeWebView) {
                                                        window.ReactNativeWebView.postMessage(JSON.stringify({
                                                            type: 'OPEN_EXTERNAL_URL',
                                                            url: smsUrl
                                                        }));
                                                    } else {
                                                        window.location.href = smsUrl;
                                                    }
                                                }}
                                                className="px-3 py-1 bg-green-600 text-white text-xs rounded-md transition-colors"
                                            >
                                                <i className="ri-message-2-line mr-1"></i>
                                                문자하기
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            {/* ⭐ 환자 상태 - 개선된 디자인 */}
                            <Card className="p-0 overflow-hidden">
                                <div className="px-1 pt-2 pb-2">
                                    <h3 className="font-semibold text-gray-900 mb-3">환자 상태</h3>

                                    {/* 기본 정보 2열 그리드 */}
                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        <div className="bg-gray-50 rounded-lg px-2 py-2.5">
                                            <p className="text-[11px] text-gray-400 mb-1">환자명</p>
                                            <p className="text-sm font-medium text-gray-900">{jobDetail.ptName}님</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg px-2 py-2.5">
                                            <p className="text-[11px] text-gray-400 mb-1">나이</p>
                                            <p className="text-sm font-medium text-gray-900">{jobDetail.ptAge}세</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg px-2 py-2.5">
                                            <p className="text-[11px] text-gray-400 mb-1">성별</p>
                                            <p className="text-sm font-medium text-gray-900">{getGenderLabel(jobDetail.ptGender)}</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg px-2 py-2.5">
                                            <p className="text-[11px] text-gray-400 mb-1">장기요양등급</p>
                                            <p className="text-sm font-medium text-gray-900">{getRankingLabel(jobDetail.ptRanking)}</p>
                                        </div>
                                        {(jobDetail.ptHeight || jobDetail.ptWeight) && (
                                            <div className="bg-gray-50 rounded-lg px-2 py-2.5">
                                                <p className="text-[11px] text-gray-400 mb-1">키 / 몸무게</p>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {jobDetail.ptHeight ? `${jobDetail.ptHeight}cm` : '-'}
                                                    {' / '}
                                                    {jobDetail.ptWeight ? `${jobDetail.ptWeight}kg` : '-'}
                                                </p>
                                            </div>
                                        )}
                                        <div className="bg-gray-50 rounded-lg px-2 py-2.5">
                                            <p className="text-[11px] text-gray-400 mb-1">거동 상태</p>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${jobDetail.ptMoveYn === 0 ? 'bg-red-400' : 'bg-green-400'
                                                    }`}></span>
                                                <p className={`text-sm font-medium ${jobDetail.ptMoveYn === 0 ? 'text-red-700' : 'text-green-700'
                                                    }`}>
                                                    {getMoveLabel(jobDetail.ptMoveYn)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 질환 및 질병 */}
                                    {jobDetail.ptDisease && (
                                        <div className="border border-gray-100 rounded-lg px-3 py-2.5 mb-2">
                                            <p className="text-[11px] text-gray-400 mb-1.5">질환 및 질병</p>
                                            <p className="text-sm text-gray-800 leading-relaxed">{jobDetail.ptDisease}</p>
                                        </div>
                                    )}

                                    {/* 특이사항 */}
                                    {jobDetail.ptOther && (
                                        <div className="border border-gray-100 rounded-lg px-3 py-2.5 mb-2">
                                            <p className="text-[11px] text-gray-400 mb-1.5">특이사항</p>
                                            <p className="text-sm text-gray-800 leading-relaxed">{jobDetail.ptOther}</p>
                                        </div>
                                    )}

                                    {/* 필요 간병 */}
                                    {jobDetail.ptCare && (
                                        <div className="mb-2">
                                            <p className="text-[11px] text-gray-400 mb-1.5">필요 간병</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {jobDetail.ptCare.split(',').map((item, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="text-xs px-2.5 py-1 rounded-md"
                                                        style={{ background: '#EEEDFE', color: '#3C3489' }}
                                                    >
                                                        {item.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* 환자 상태 */}
                                    {jobDetail.ptStatus && (
                                        <div>
                                            <p className="text-[11px] text-gray-400 mb-1.5">환자 상태</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {jobDetail.ptStatus.split(',').map((item, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="text-xs px-2.5 py-1 rounded-md"
                                                        style={{ background: '#FCEBEB', color: '#791F1F' }}
                                                    >
                                                        {item.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>

                        </div>
                    )}

                    {activeTab === 'location' && (
                        <div className="space-y-4">
                            {/* ⭐ 네이버 정적 지도 - 프록시 사용 */}
                            <Card className="p-0 overflow-hidden">
                                <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                                    {jobDetail.xdVal && jobDetail.ydVal ? (
                                        <img
                                            src={`${BASE_URL}/care/mapStatic.do?x=${jobDetail.xdVal}&y=${jobDetail.ydVal}&w=400&h=264&level=15`}
                                            alt="간병 장소 지도"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                console.log('[공고 상세] 지도 이미지 로드 실패');
                                                (e.target as HTMLImageElement).style.display = 'none';
                                                const parent = (e.target as HTMLElement).parentElement;
                                                if (parent) {
                                                    parent.innerHTML = `
                                    <div class="w-full h-full flex items-center justify-center bg-gray-100">
                                        <div class="text-center">
                                            <i class="ri-map-pin-off-line text-4xl text-gray-400 mb-2"></i>
                                            <p class="text-gray-500 text-sm">지도를 불러올 수 없습니다</p>
                                        </div>
                                    </div>
                                `;
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div className="text-center">
                                            <i className="ri-map-pin-line text-4xl text-gray-400 mb-2"></i>
                                            <p className="text-gray-500">위치 정보가 없습니다.</p>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* 주소 정보 */}
                            <Card>
                                <h3 className="font-semibold text-gray-900 mb-3">위치 정보</h3>
                                <div className="space-y-3">
                                    {/* 만남 장소 */}
                                    <div className="flex items-start space-x-3">
                                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-100">
                                            <i className="ri-map-pin-line text-orange-600"></i>
                                        </div>
                                        <div className="flex-1">
                                            {jobDetail.careWhat === 3 && (
                                                <p className="text-xs font-semibold text-purple-600 mb-1">만남 장소</p>
                                            )}
                                            <p className="font-medium text-gray-900">{jobDetail.careArea}</p>
                                            {jobDetail.careAreaDetail && (
                                                <p className="text-sm text-gray-600 mt-1">{jobDetail.careAreaDetail}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* ⭐ 병원동행: 방문 장소 */}
                                    {jobDetail.careWhat === 3 && jobDetail.visitArea && (
                                        <div className="flex items-start space-x-3">
                                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100">
                                                <i className="ri-hospital-line text-purple-600"></i>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs font-semibold text-purple-600 mb-1">방문 장소</p>
                                                <p className="font-medium text-gray-900">{jobDetail.visitArea}</p>
                                                {jobDetail.visitAreaDetail && (
                                                    <p className="text-sm text-gray-600 mt-1">{jobDetail.visitAreaDetail}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'requirements' && (
                        <div className="space-y-4">
                            {/* ⭐ 근무 시간 - 개선된 디자인 */}
                            <Card>
                                <h3 className="font-semibold text-gray-900 mb-4">근무 시간</h3>
                                <div className="space-y-0">
                                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                        <div className="flex items-center space-x-2">
                                            <i className="ri-hospital-line text-gray-400 text-sm"></i>
                                            <span className="text-[13px] text-gray-500 uppercase tracking-wide">간병 유형</span>
                                        </div>
                                        <span className="text-gray-900 font-medium">{getCareKindLabel(jobDetail.careKind)}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                        <div className="flex items-center space-x-2">
                                            <i className="ri-calendar-check-line text-gray-400 text-sm"></i>
                                            <span className="text-[13px] text-gray-500 uppercase tracking-wide">시작일</span>
                                        </div>
                                        <span className="text-gray-900">{jobDetail.startDate}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                        <div className="flex items-center space-x-2">
                                            <i className="ri-calendar-close-line text-gray-400 text-sm"></i>
                                            <span className="text-[13px] text-gray-500 uppercase tracking-wide">종료일</span>
                                        </div>
                                        <span className="text-gray-900">{jobDetail.endDate}</span>
                                    </div>
                                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                        <div className="flex items-center space-x-2">
                                            <i className="ri-time-line text-gray-400 text-sm"></i>
                                            <span className="text-[13px] text-gray-500 uppercase tracking-wide">근무 시간</span>
                                        </div>
                                        <span className="text-gray-900">{jobDetail.startCareTime} ~ {jobDetail.endCareTime}</span>
                                    </div>
                                    {jobDetail.careDays && (
                                        <div className="flex items-start justify-between py-3">
                                            <div className="flex items-center space-x-2">
                                                <i className="ri-calendar-2-line text-gray-400 text-sm"></i>
                                                <span className="text-[13px] text-gray-500 uppercase tracking-wide">
                                                    {jobDetail.careWhat === 3 ? '동행 일수' : '간병 일수'}
                                                </span>
                                            </div>
                                            <span className="text-gray-900">{getPeriod(jobDetail.careKind, jobDetail.careDays, jobDetail.startDate, jobDetail.endDate)}일</span>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* 지원 정보 */}
                            <Card>
                                <h3 className="font-semibold text-gray-900 mb-3">지원 정보</h3>
                                <div className="space-y-3">

                                    {/* 매칭 방식 뱃지 */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500">매칭 방식</span>
                                        {jobDetail.matchType === 'F' ? (
                                            <span className="text-xs font-medium px-3 py-1 rounded-md"
                                                style={{ background: '#EAF3DE', color: '#3B6D11' }}>
                                                정찰제
                                            </span>
                                        ) : (
                                            <span className="text-xs font-medium px-3 py-1 rounded-md"
                                                style={{ background: '#FAEEDA', color: '#854F0B' }}>
                                                입찰제
                                            </span>
                                        )}
                                    </div>

                                    <div className="border-t border-gray-100"></div>

                                    {jobDetail.matchType === 'F' ? (
                                        // ===== 정찰제(F) =====
                                        (() => {
                                            const [sh, sm] = jobDetail.startCareTime.split(':').map(Number);
                                            const [eh, em] = jobDetail.endCareTime.split(':').map(Number);
                                            const dailyHours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                                            const dailyPay = jobDetail.oneDayMoney * dailyHours;

                                            return (
                                                <>
                                                    {/* 금액 요약 박스 */}
                                                    <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--color-background-secondary, #f9f9f9)' }}>
                                                        <div className="flex items-baseline justify-between">
                                                            <span className="text-xs text-gray-500">
                                                                {jobDetail.careKind === 2 ? '보호자 제시 일당' : '보호자 제시 시급'}
                                                            </span>
                                                            <span className="text-lg font-semibold" style={{ color: '#854F0B' }}>
                                                                {jobDetail.oneDayMoney.toLocaleString()}원
                                                            </span>
                                                        </div>
                                                        {jobDetail.careKind !== 2 && (
                                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                                <span>하루 간병비 ({dailyHours}시간)</span>
                                                                <span className="text-gray-700">{dailyPay.toLocaleString()}원</span>
                                                            </div>
                                                        )}
                                                        {jobDetail.totalMoney > 0 && (
                                                            <div className="border-t border-gray-200 pt-2 flex items-baseline justify-between">
                                                                <span className="text-sm font-medium text-gray-700">예상 총 간병비</span>
                                                                <span className="text-base font-semibold text-gray-900">
                                                                    {jobDetail.totalMoney.toLocaleString()}원
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* 정찰제 + 지원완료 뱃지 */}
                                                    {(jobDetail.estimateYn === 1 || !!jobDetail.estimateNo) && (
                                                        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg"
                                                            style={{ background: '#EAF3DE' }}>
                                                            <i className="ri-check-circle-fill text-sm mt-0.5" style={{ color: '#3B6D11' }}></i>
                                                            <div>
                                                                <p className="text-xs font-medium" style={{ color: '#27500A' }}>지원 완료</p>
                                                                <p className="text-xs mt-0.5" style={{ color: '#3B6D11' }}>
                                                                    정찰제 공고로 보호자 제시 금액으로 지원되었습니다.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()
                                    ) : (
                                        // ===== 입찰제(B) =====
                                        (jobDetail.estimateYn === 1 || !!jobDetail.estimateNo) ? (
                                            (() => {
                                                const timeMoney = jobDetail.oneDayMoney;
                                                const [sh, sm] = jobDetail.startCareTime.split(':').map(Number);
                                                const [eh, em] = jobDetail.endCareTime.split(':').map(Number);
                                                const dailyHours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                                                const period = getPeriod(
                                                    jobDetail.careKind,
                                                    jobDetail.careDays,
                                                    jobDetail.startDate,
                                                    jobDetail.endDate
                                                );
                                                const dailyPay = jobDetail.careKind === 2 ? timeMoney : timeMoney * dailyHours;
                                                const totalMoney = jobDetail.careKind === 2 ? timeMoney * period : timeMoney * dailyHours * period;

                                                return (
                                                    <>
                                                        {/* 금액 요약 박스 */}
                                                        <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--color-background-secondary, #f9f9f9)' }}>
                                                            <div className="flex items-baseline justify-between">
                                                                <span className="text-xs text-gray-500">
                                                                    {jobDetail.careKind === 2 ? '내가 제출한 일당' : '내가 제출한 시급'}
                                                                </span>
                                                                <span className="text-lg font-semibold" style={{ color: '#854F0B' }}>
                                                                    {timeMoney.toLocaleString()}원
                                                                </span>
                                                            </div>
                                                            {jobDetail.careKind !== 2 && (
                                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                                    <span>하루 간병비 ({dailyHours}시간)</span>
                                                                    <span className="text-gray-700">{dailyPay.toLocaleString()}원</span>
                                                                </div>
                                                            )}
                                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                                <span>간병 일수</span>
                                                                <span className="text-gray-700">{period}일</span>
                                                            </div>
                                                            <div className="border-t border-gray-200 pt-2 flex items-baseline justify-between">
                                                                <span className="text-sm font-medium text-gray-700">예상 총 간병비</span>
                                                                <span className="text-base font-semibold text-gray-900">
                                                                    {totalMoney.toLocaleString()}원
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* 견적 제출 완료 뱃지 */}
                                                        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg"
                                                            style={{ background: '#EAF3DE' }}>
                                                            <i className="ri-check-circle-fill text-sm mt-0.5" style={{ color: '#3B6D11' }}></i>
                                                            <div>
                                                                <p className="text-xs font-medium" style={{ color: '#27500A' }}>견적 제출 완료</p>
                                                                <p className="text-xs mt-0.5" style={{ color: '#3B6D11' }}>
                                                                    수정하려면 하단의 '견적 수정하기' 버튼을 눌러주세요.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()
                                        ) : (
                                            // 입찰제 + 미지원
                                            <div className="flex items-start gap-2 p-3 rounded-lg bg-gray-50">
                                                <i className="ri-information-line text-sm mt-0.5 text-gray-400"></i>
                                                <p className="text-xs text-gray-500 leading-relaxed">
                                                    견적 금액을 직접 제출해주세요. 보호자가 견적을 확인한 후 연락드릴 예정입니다.
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* 안내 메시지 */}
                    <Card className="mt-6 mb-6 bg-blue-50 border-blue-200">
                        <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100">
                                <i className="ri-information-line text-blue-600"></i>
                            </div>
                            <div>
                                <h4 className="font-medium text-blue-900 mb-1">지원 안내</h4>
                                {jobDetail.matchType === 'F' ? (
                                    <p className="text-sm text-blue-700">
                                        정찰제 공고입니다. 보호자가 제시한 금액으로 지원됩니다.
                                        지원하기 버튼을 클릭하면 보호자에게 알림이 전송됩니다.
                                    </p>
                                ) : (
                                    <p className="text-sm text-blue-700">
                                        입찰제 공고입니다. 지원하기 버튼을 클릭하여 견적 금액을 제출해주세요.
                                        보호자가 견적을 확인한 후 연락드릴 예정입니다.
                                    </p>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* 하단 고정 버튼 */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                <Button
                    className={`w-full text-white ${isButtonDisabled
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-orange-500 hover:bg-orange-600'
                        }`}
                    onClick={handleApply}
                    disabled={isButtonDisabled}
                >
                    <i className={`${fromSentQuotes && jobDetail.matchType === 'B'
                        ? 'ri-edit-line'
                        : fromSentQuotes
                            ? 'ri-eye-line'
                            : 'ri-send-plane-line'
                        } mr-2`}></i>
                    {getButtonText()}
                </Button>
            </div>
        </div>
    );
}